import type { RootState } from "../store/rootReducer";
import {
  clearDirty,
  setLastPulledAt,
  setSyncStatus,
  setSyncError,
  setLastSyncAt,
  clearPrefsDirty,
  setPrefsLastPulledAt,
  setPrefsPulled,
  SyncTable,
} from "../store/slices/syncSlice";
import {
  selectSyncedPreferences,
  applySyncedPreferences,
} from "../store/slices/preferencesSlice";
import { registerRunSync } from "../store/syncMiddleware";
import {
  SYNC_TABLE_ORDER,
  PURGE_TABLES,
  dbName,
  toRow,
  fromRow,
  upsertActions,
  removeActions,
} from "./tableRegistry";
import type { SyncManagerDeps, SyncOutcome } from "./types";

const EPOCH = "1970-01-01T00:00:00Z";

// The per-user preferences singleton (one row keyed by user_id, a jsonb blob).
const USER_PREFS_TABLE = "user_preferences";

// How long a soft-deleted row lingers locally before the purge hard-removes it.
// LOCAL storage-hygiene only — the server keeps tombstones indefinitely, so every
// device still eventually pulls each deletion. Purging only ever drops rows that
// are already pushed (the not-dirty guard), so this can't resurrect anything.
const TOMBSTONE_RETENTION_DAYS = 90;
const TOMBSTONE_RETENTION_MS = TOMBSTONE_RETENTION_DAYS * 24 * 60 * 60 * 1000;

// The watchdog firing means the sync is genuinely stuck — distinct from an
// incidental network "timeout" blip. Its own error type lets isTransient keep
// reporting it to monitoring instead of filtering it out by message match, so a
// recurring/systemic hang (e.g. a request to an unreachable host) actually alerts.
class SyncTimeoutError extends Error {
  readonly isWatchdogTimeout = true;
  constructor(message = "Sync timed out. Check your connection") {
    super(message);
    this.name = "SyncTimeoutError";
  }
}

// Watchdog: Supabase/Storage/file calls have no timeout of their own, so a single
// hung request would leave `running` stuck true and silently skip every future
// sync. Racing the whole run against this guarantees `running` is always released.
const SYNC_TIMEOUT_MS = 45_000;
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new SyncTimeoutError()), ms)),
  ]);
}

// Builds a sync manager bound to one app's store, Supabase client, and platform
// adapters. Returns { runSync } and wires runSync into the dirty-tracking
// middleware (so a debounced push fires after local edits settle). The push/pull/
// LWW/cursor/purge logic here is platform-agnostic; only image handling and the
// connectivity check are delegated to the injected adapters.
export function createSyncManager({
  store,
  supabase,
  net,
  images,
  debug = false,
  onError,
}: SyncManagerDeps): { runSync: () => Promise<SyncOutcome> } {
  let running = false;

  // Report a caught sync error to the injected monitor, normalised to a real Error
  // (Supabase/PostgREST errors are plain objects, which Sentry can't unwrap into a
  // message) and carrying the PostgREST code/details as context.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- caught errors are untyped
  const report = (e: any, ctx: { op: string; [k: string]: unknown }) => {
    const err = e instanceof Error ? e : new Error(e?.message ?? "Sync error");
    onError?.(err, { ...ctx, code: e?.code, details: e?.details });
  };

  // Transient failures the next sync retries and that self-heal — not worth a
  // monitoring alert. PGRST303 ("JWT issued at future") is post-refresh clock skew
  // between Supabase's auth and REST servers; plus the usual network blips.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- caught errors are untyped
  const isTransient = (e: any): boolean => {
    // The watchdog firing is systemic, not a self-healing blip — always report it,
    // even though its message contains "timed out".
    if (e instanceof SyncTimeoutError) return false;
    const msg = String(e?.message ?? e ?? "");
    return (
      e?.code === "PGRST303" ||
      /network request failed|failed to fetch|load failed|timed out|timeout/i.test(
        msg,
      )
    );
  };

  const state = () => store.getState() as RootState;

  // Entity dictionaries are keyed by id with per-table shapes; loose at this boundary.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  function entitiesOf(table: SyncTable): Record<string, any> {
    return state()[table].entities as Record<string, any>;
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  function tableSync(table: SyncTable): { dirtyIds: string[]; lastPulledAt: string | null } {
    return state().sync.byTable[table] ?? { dirtyIds: [], lastPulledAt: null };
  }

  // --- push --------------------------------------------------------------
  async function pushAll(userId: string): Promise<string[]> {
    const failures: string[] = [];
    for (const table of SYNC_TABLE_ORDER) {
      const dirtyIds = tableSync(table).dirtyIds;
      if (!dirtyIds.length) continue;

      const entities = entitiesOf(table);
      const now = new Date().toISOString();
      const rows = dirtyIds
        .map((id) => entities[id]) // the local row (incl. deletedAt tombstones)
        .filter(Boolean)
        .filter((r) => !r.isSystem) // never push app-defined system rows
        .map((r) => {
          const row = toRow(table, r); // camelCase -> snake_case
          return {
            ...row,
            // Stamp the current user as owner so the row satisfies RLS
            // `user_id = auth.uid()`, regardless of any stale/missing value.
            user_id: userId,
            // created_at/updated_at are NOT NULL with a default, but a default only
            // applies to omitted columns — never to an explicit null. In a batch
            // upsert PostgREST unions columns and fills missing ones with null, so
            // a row lacking these would violate the constraint. Always supply them.
            created_at: row.created_at ?? row.updated_at ?? now,
            updated_at: row.updated_at ?? now,
          };
        });

      try {
        if (rows.length) {
          const { error } = await supabase.from(dbName(table)).upsert(rows);
          if (error) throw error;
        }
        // Only clear once the upsert succeeded.
        store.dispatch(clearDirty({ table, ids: dirtyIds }));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- caught error is untyped
      } catch (e: any) {
        // Don't let one table's failed push abort the whole sync. Leave these rows
        // dirty (they retry next time) and keep going, so other tables — and the
        // *pull* — still run.
        failures.push(`${dbName(table)}: ${e?.message ?? String(e)}`);
        if (!isTransient(e)) report(e, { op: "push", table: dbName(table) });
      }
    }
    return failures;
  }

  // --- pull --------------------------------------------------------------
  // Pull each table independently. A single table's failure (RLS denial, a column
  // missing because a migration hasn't been applied, a transient blip) must NOT
  // abort the other tables' pulls or the overall sync — mirror pushAll and record
  // the failure instead, so one bad table degrades gracefully rather than blocking
  // everything. That table's cursor isn't advanced, so it simply re-pulls next sync.
  async function pullAll(): Promise<string[]> {
    const failures: string[] = [];
    for (const table of SYNC_TABLE_ORDER) {
      try {
        const since = tableSync(table).lastPulledAt ?? EPOCH;
        const { data, error } = await supabase
          .from(dbName(table))
          .select("*")
          .gt("updated_at", since)
          .order("updated_at", { ascending: true });
        if (error) throw error;
        if (!data?.length) continue;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- rows are heterogeneous across tables
        const toApply: any[] = [];
        let oldestRejected: string | null = null; // raw updated_at of the oldest dropped row
        for (const row of data) {
          const incoming = fromRow(table, row); // snake_case -> camelCase
          if (shouldApply(table, incoming)) {
            toApply.push(incoming); // dirty-aware LWW: server wins / brand-new
          } else if (oldestRejected === null || row.updated_at < oldestRejected) {
            oldestRejected = row.updated_at; // dropped: local dirty row won this round
          }
        }

        if (toApply.length) store.dispatch(upsertActions[table](toApply));

        // Cursor: normally jump to the newest row seen. But NEVER advance past a row
        // we rejected — otherwise `updated_at > since` would never return it again and
        // that server change would be lost if the local dirty row that beat it fails
        // to push. Cap the cursor at the newest row strictly older than the oldest
        // rejected one, so it (and anything newer) re-pulls next sync until resolved.
        let nextCursor = data[data.length - 1].updated_at;
        if (oldestRejected !== null) {
          const rejectedAt = new Date(oldestRejected).getTime();
          const olderThanRejected = data
            .map((r) => r.updated_at)
            .filter((t) => new Date(t).getTime() < rejectedAt);
          nextCursor = olderThanRejected.length
            ? olderThanRejected[olderThanRejected.length - 1]
            : since;
        }
        store.dispatch(setLastPulledAt({ table, at: nextCursor }));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- caught error is untyped
      } catch (e: any) {
        failures.push(`${dbName(table)}: ${e?.message ?? String(e)}`);
        if (!isTransient(e)) report(e, { op: "pull", table: dbName(table) });
      }
    }
    return failures;
  }

  // --- preferences (per-user singleton) ----------------------------------
  // Account-scoped preferences don't fit the entity/dirty-per-id model — they're
  // ONE jsonb blob per user. So they get their own tiny push/pull with a single
  // dirty flag + cursor. The `data` column is the SYNCED_PREF_KEYS subset.

  // Push the current synced-preferences blob if it changed locally. Returns an
  // error string on failure (added to the run's push failures), else null.
  async function pushPreferences(userId: string): Promise<string | null> {
    if (!state().sync.prefsDirty) return null; // nothing changed since last push
    const data = selectSyncedPreferences(state());
    try {
      // user_id is the PK (one row per user); server trigger stamps updated_at.
      const { error } = await supabase
        .from(USER_PREFS_TABLE)
        .upsert({ user_id: userId, data }, { onConflict: "user_id" });
      if (error) throw error;
      store.dispatch(clearPrefsDirty());
      return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- caught error is untyped
    } catch (e: any) {
      if (!isTransient(e)) report(e, { op: "push-prefs" });
      return `${USER_PREFS_TABLE}: ${e?.message ?? String(e)}`;
    }
  }

  // Pull the server's preferences blob if it's newer than our cursor. A locally
  // dirty (unpushed) change is protected — keep local and don't advance the cursor
  // so the server copy re-pulls once our change lands (mirrors the entity LWW).
  // Returns an error string on failure (added to the run's push/pull failures)
  // rather than throwing, so a prefs-pull blip can't abort the whole sync.
  async function pullPreferences(): Promise<string | null> {
    const since = state().sync.prefsLastPulledAt ?? EPOCH;
    const { data, error } = await supabase
      .from(USER_PREFS_TABLE)
      .select("data, updated_at")
      .gt("updated_at", since)
      .order("updated_at", { ascending: true })
      .limit(1);
    if (error) {
      if (!isTransient(error)) report(error, { op: "pull-prefs" });
      return `${USER_PREFS_TABLE}: ${error.message ?? String(error)}`;
    }
    const row = data?.[0];
    // Apply the account's real preferences BEFORE flipping prefsPulled — otherwise
    // there's a window where prefsPulled is true but hasSeenOnboarding is still the
    // meaningless local default (false), which flashes the onboarding gate for a
    // returning user mid-sync. `prefsDirty` means a local unpushed change wins this
    // round, so skip the server copy (it re-pulls next time).
    if (row && !state().sync.prefsDirty) {
      store.dispatch(applySyncedPreferences(row.data ?? {}));
      store.dispatch(setPrefsLastPulledAt(row.updated_at));
    }
    // Now we know the account's real preferences (even with no row yet → a
    // genuinely new user, who correctly keeps the local default and sees the tour).
    store.dispatch(setPrefsPulled(true));
    return null;
  }

  // Conflict resolution — fully server-authoritative, no client clocks. A locally
  // dirty (unpushed) row is protected: keep local and skip the server version this
  // round. It stays dirty and re-pulls (the cursor won't advance past a rejected
  // row) until the next successful push, at which point the server stamps
  // updated_at and the truth flows back on the following pull. Everything else:
  // the server wins. This makes LWW "last device to successfully push wins"
  // (sync order) rather than "newest wall-clock wins", so it's immune to device
  // clock skew.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- incoming row shape varies per table
  function shouldApply(table: SyncTable, incoming: any): boolean {
    const local = entitiesOf(table)[incoming.id];
    if (!local) return true; // brand-new row from the server
    return !tableSync(table).dirtyIds.includes(incoming.id); // dirty → keep local
  }

  // --- purge -------------------------------------------------------------
  // Hard-remove long-dead soft-deleted rows from this device's store. Local-only
  // cleanup: the server keeps its tombstones, so dropping them here never loses a
  // deletion. Gated on (1) past retention window and (2) NOT still dirty (an
  // unpushed deletion must reach the server first, else another device resurrects it).
  function purgeTombstones(): void {
    const cutoff = Date.now() - TOMBSTONE_RETENTION_MS;

    for (const table of PURGE_TABLES) {
      const remove = removeActions[table];
      if (!remove) continue;

      const entities = entitiesOf(table);
      const dirty = new Set(tableSync(table).dirtyIds);

      const purgeable = Object.keys(entities).filter((id) => {
        const row = entities[id];
        if (!row?.deletedAt) return false; // only tombstones
        if (dirty.has(id)) return false; // unpushed deletion — must propagate first
        return new Date(row.deletedAt).getTime() < cutoff; // past retention window
      });

      if (purgeable.length) store.dispatch(remove(purgeable));
    }
  }

  // --- orchestration -----------------------------------------------------
  async function runSync(): Promise<SyncOutcome> {
    if (debug) {
      const dirty = Object.entries(state().sync.byTable)
        .filter(([, t]) => t.dirtyIds.length)
        .map(([name, t]) => `${name}[${t.dirtyIds.join(", ")}]`);
      if (dirty.length) console.warn("[sync] runSync; dirty:", dirty.join("  "));
    }
    if (running) {
      if (debug) console.warn("[sync] skipped: already running (stuck?)");
      return { ok: true, skipped: true }; // single-flight: never overlap two syncs
    }

    // Set running before the preflight so single-flight covers it too, and wrap
    // the ENTIRE run — including getSession()/isOnline() — in the watchdog. Those
    // preflight calls can themselves make network requests (a token refresh) that
    // would otherwise hang with no timeout, outside any guard.
    running = true;
    try {
      return await withTimeout<SyncOutcome>(
        (async (): Promise<SyncOutcome> => {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session) {
            if (debug) console.warn("[sync] skipped: no session");
            return { ok: true, skipped: true }; // only when signed in
          }

          // Skip when offline: avoids doomed network calls from background triggers.
          if (!(await net.isOnline())) {
            if (debug) console.warn("[sync] skipped: offline");
            return { ok: false, message: "You're offline" };
          }

          store.dispatch(setSyncStatus("syncing"));
          const imageFailures = await images.uploadPendingImages(session.user.id);
          const pushFailures = await pushAll(session.user.id);
          const prefPushFailure = await pushPreferences(session.user.id);
          if (prefPushFailure) pushFailures.push(prefPushFailure);
          // Server changes down — runs even if some pushes failed. Both are
          // per-table resilient and report their own failures.
          const pullFailures = await pullAll();
          const prefPullFailure = await pullPreferences();
          if (prefPullFailure) pullFailures.push(prefPullFailure);
          purgeTombstones();
          await images.cacheRemoteImages();

          store.dispatch(setLastSyncAt(new Date().toISOString()));

          if (pushFailures.length > 0) {
            console.warn("[sync] push failures:", pushFailures);
            const message = `Couldn't push ${pushFailures.length} change${
              pushFailures.length > 1 ? "s" : ""
            }: ${pushFailures[0]}`;
            store.dispatch(setSyncError(message));
            store.dispatch(setSyncStatus("error"));
            return { ok: false, message };
          }
          if (pullFailures.length > 0) {
            console.warn("[sync] pull failures:", pullFailures);
            const message = `Couldn't fetch ${pullFailures.length} update${
              pullFailures.length > 1 ? "s" : ""
            }: ${pullFailures[0]}`;
            store.dispatch(setSyncError(message));
            store.dispatch(setSyncStatus("error"));
            return { ok: false, message };
          }
          if (imageFailures > 0) {
            const message = `${imageFailures} photo${imageFailures > 1 ? "s" : ""} couldn't upload, will retry`;
            store.dispatch(setSyncError(message));
            store.dispatch(setSyncStatus("error"));
            return { ok: false, message };
          }
          store.dispatch(setSyncError(null));
          store.dispatch(setSyncStatus("idle"));
          return { ok: true };
        })(),
        SYNC_TIMEOUT_MS,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- caught error is untyped
    } catch (e: any) {
      const message = e?.message ?? "sync failed";
      if (debug) console.warn("[sync] failed:", message);
      if (!isTransient(e)) report(e, { op: "run" });
      store.dispatch(setSyncError(message));
      store.dispatch(setSyncStatus("error"));
      return { ok: false, message };
    } finally {
      running = false;
    }
  }

  // Give syncMiddleware a reference to runSync without importing this module
  // (avoids the store → syncMiddleware → SyncManager → store import cycle).
  registerRunSync(async () => {
    await runSync();
  });

  return { runSync };
}
